/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, LinearProgress } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ItemInterface } from 'react-sortablejs';
import { ReactSortable } from 'react-sortablejs';
import type { SortableEvent } from 'sortablejs';
import Sortable from 'sortablejs';
import type { CoreAppStore } from '@opengeoweb/store';
import { layerTypes, mapActions, mapSelectors } from '@opengeoweb/store';
import { LayerType } from '@opengeoweb/webmap';
import type { LayerManagerCustomSettings } from '../LayerManagerUtils';
import DragHandle from './LayerRow/DragHandle/DragHandle';
import LayerRowConnect from './LayerRow/LayerRowConnect';

interface LayerContainerRowProps extends React.ComponentProps<typeof Grid> {
  mapId: string;
  settings?: LayerManagerCustomSettings['content'];
  collapsedColumns?: Record<string, boolean>;
}

interface CustomSortableEvent extends SortableEvent {
  mapId: string;
  oldIndex: number;
  newIndex: number;
  layerType: string;
}

const LayerContainerRow: React.FC<LayerContainerRowProps> = React.memo(
  ({
    mapId,
    settings,
    collapsedColumns,
    ...gridProps
  }: LayerContainerRowProps) => {
    const dispatch = useDispatch();
    const [isPending, startTransition] = React.useTransition();
    // Separate map layers and feature layers
    const mapLayerIds = useSelector((store: CoreAppStore) =>
      mapSelectors.getLayerIds(store, mapId),
    );
    const featureLayers = useSelector((store: CoreAppStore) =>
      mapSelectors.getMapFeatureLayers(store, mapId),
    );

    // State for drag and display for each list
    const [activeDragIndexMap, setActiveDragIndexMap] = React.useState<
      number | null
    >(null);
    const [activeDragIndexFeature, setActiveDragIndexFeature] = React.useState<
      number | null
    >(null);
    const [rowsToDisplayMap, setRowsToDisplayMap] = React.useState(0);
    const [rowsToDisplayFeature, setRowsToDisplayFeature] = React.useState(0);

    const featureLayerIds = React.useMemo(() => {
      return featureLayers
        .filter((layer) => layer.name !== undefined && layer.id !== undefined)
        .map((layer) => layer.id);
    }, [featureLayers]);

    const layerMove = React.useCallback(
      ({ mapId, oldIndex, newIndex, layerType }: CustomSortableEvent) =>
        dispatch(
          mapActions.layerMoveLayer({
            mapId,
            oldIndex,
            newIndex,
            origin: layerTypes.LayerActionOrigin.layerManager,
            layerType,
          }),
        ),
      [dispatch],
    );

    // Drag handlers for map layers
    const onSetListMap = (): void => {};
    const onStartDragMap = React.useCallback(
      ({ oldIndex }: CustomSortableEvent) => {
        setActiveDragIndexMap(oldIndex);
        if (Sortable.ghost) {
          Sortable.ghost.style.opacity = '1';
        }
      },
      [],
    );
    const onEndDragMap = React.useCallback(() => {
      if (activeDragIndexMap !== null) {
        setActiveDragIndexMap(null);
      }
    }, [activeDragIndexMap]);
    const onSortEndMap = ({
      oldIndex,
      newIndex,
    }: CustomSortableEvent): void => {
      setActiveDragIndexMap(null);
      layerMove({
        mapId,
        oldIndex,
        newIndex,
        layerType: LayerType.mapLayer,
      } as CustomSortableEvent);
    };

    // Drag handlers for feature layers
    const onSetListFeature = (): void => {};
    const onStartDragFeature = React.useCallback(
      ({ oldIndex }: CustomSortableEvent) => {
        setActiveDragIndexFeature(oldIndex);
        if (Sortable.ghost) {
          Sortable.ghost.style.opacity = '1';
        }
      },
      [],
    );
    const onEndDragFeature = React.useCallback(() => {
      if (activeDragIndexFeature !== null) {
        setActiveDragIndexFeature(null);
      }
    }, [activeDragIndexFeature]);
    const onSortEndFeature = ({
      oldIndex,
      newIndex,
    }: CustomSortableEvent): void => {
      setActiveDragIndexFeature(null);
      layerMove({
        mapId,
        oldIndex,
        newIndex,
        layerType: LayerType.featureLayer,
      } as CustomSortableEvent);
    };

    // Display logic for each list
    const isSortingMap = activeDragIndexMap !== null;
    const isSortingFeature = activeDragIndexFeature !== null;
    const isDragDisabledMap = mapLayerIds.length === 1;
    const isDragDisabledFeature = featureLayerIds.length === 1;

    const mapLayerContents = mapLayerIds.map((layerId) => (
      <LayerRowConnect
        collapsedColumns={collapsedColumns}
        dragHandle={
          <DragHandle
            hideTooltip={isSortingMap || isDragDisabledMap}
            icon={settings?.dragHandle?.icon}
            isDisabled={isDragDisabledMap}
            isSorting={isSortingMap}
            tooltipTitle={settings?.dragHandle?.tooltipTitle}
          />
        }
        key={`layerRowConnect-map-${layerId}`}
        layerId={layerId}
        mapId={mapId}
        settings={settings}
      />
    ));

    const featureLayerContents = featureLayerIds.map((layerId) => (
      <LayerRowConnect
        collapsedColumns={collapsedColumns}
        dragHandle={
          <DragHandle
            hideTooltip={isSortingFeature || isDragDisabledFeature}
            icon={settings?.dragHandle?.icon}
            isDisabled={isDragDisabledFeature}
            isSorting={isSortingFeature}
            tooltipTitle={settings?.dragHandle?.tooltipTitle}
          />
        }
        key={`layerRowConnect-feature-${layerId}`}
        layerId={layerId!}
        mapId={mapId}
        settings={settings}
      />
    ));

    React.useEffect(() => {
      if (rowsToDisplayMap < mapLayerIds.length) {
        startTransition(() => {
          setRowsToDisplayMap(rowsToDisplayMap + mapLayerIds.length);
        });
      }
      if (rowsToDisplayFeature < featureLayerIds.length) {
        startTransition(() => {
          setRowsToDisplayFeature(
            rowsToDisplayFeature + featureLayerIds.length,
          );
        });
      }
    }, [
      mapLayerIds.length,
      featureLayerIds.length,
      rowsToDisplayMap,
      rowsToDisplayFeature,
    ]);

    return (
      <Grid
        className="layerContainerRow"
        container
        data-testid="layerContainerRow"
        sx={{
          width: '100%',
          maxHeight: 'calc(100% - 10px)',
          overflow: 'auto',
          '& .sortable-chosen': {
            boxShadow: 1,
          },
          '& .sortable-ghost': {
            opacity: '0.5 !important',
          },
        }}
        {...gridProps}
      >
        {/* Feature Layers Sortable List */}
        {rowsToDisplayFeature > 0 && (
          <ReactSortable
            animation={200}
            direction="vertical"
            forceFallback={false}
            handle=".handle"
            list={featureLayerIds.map(
              (layerId): ItemInterface => ({ id: layerId! }),
            )}
            onEnd={onEndDragFeature}
            onSort={onSortEndFeature as (event: SortableEvent) => void}
            onStart={onStartDragFeature as (event: SortableEvent) => void}
            setList={onSetListFeature}
            style={{ width: '100%', marginBottom: '8px' }}
            tag="div"
          >
            {featureLayerContents.slice(0, rowsToDisplayFeature)}
          </ReactSortable>
        )}
        {/* Map Layers Sortable List */}
        <ReactSortable
          animation={200}
          direction="vertical"
          forceFallback={false}
          handle=".handle"
          list={mapLayerIds.map((layerId): ItemInterface => ({ id: layerId }))}
          onEnd={onEndDragMap}
          onSort={onSortEndMap as (event: SortableEvent) => void}
          onStart={onStartDragMap as (event: SortableEvent) => void}
          setList={onSetListMap}
          style={{ width: '100%' }}
          tag="div"
        >
          {isPending ? (
            <LinearProgress data-testid="loading-bar-map" />
          ) : (
            mapLayerContents.slice(0, rowsToDisplayMap)
          )}
        </ReactSortable>
      </Grid>
    );
  },
);

/**
 * A Connected container component for rendering a sortable set of rows
 *
 * Expects the following props:
 * @param {string} mapId mapId: string - Id of the map
 * @param {LayerManagerWidth} layerManagerWidth layerManagerWidth: LayerManagerWidth - Width of the Layer Manager
 * @example
 * ``` <LayerContainerRow mapId={mapId} layerManagerWidth={LayerManagerWidth.lg} />```
 */
export default LayerContainerRow;
